import { Plus } from "lucide-react";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import type { ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../../assets/copy/tips";
import Loader from "../../../../../components/Loader";
import ReferenceSummaryBox from "../../../../../components/ResumeBuilder/ReferenceSummaryBox/ReferenceSummaryBox";
import Tips from "../../../../../components/Tips";
import ResumeContext from "../../../../../context/ResumeContext";

const ReferenceSummary = () => {
	const navigate = useNavigate();
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;
	const { references } = resumeData.extras;

	// `journal` only exists once the server resume has hydrated into the context.
	// Until then `references` is the empty array from `resumeInitialData.js`, which
	// is indistinguishable from a reader who has saved no referee — so opening this
	// screen directly, or reloading it, would push a reader with saved referees
	// into the blank form.
	const hasLoadedResume = Boolean(resumeData.journal);
	const hasReferences = references.length > 0;

	// A summary of nothing is a dead end: the reader still has to find the CTA to
	// get anywhere. Open the form itself for the first referee, and let the
	// "Add another reference" CTA take over once one is saved.
	useEffect(() => {
		if (!hasLoadedResume || hasReferences) return;

		// `replace` keeps the empty summary out of history. Pushing it would trap the
		// form's back and "Skip for now" — both land here, and this bounces straight
		// back to the form.
		navigate("/resume/reference-information", { replace: true });
	}, [hasLoadedResume, hasReferences, navigate]);

	const addAnotherEduHandler = () => {
		navigate("/resume/reference-information");
	};

	if (!hasLoadedResume || !hasReferences) return <Loader />;

	return (
		<div className="min-h-screen">
			<div className="w-full md:max-w-3xl">
				<div className="mb-2 flex justify-end">
					<Tips content={tips.referees} />
				</div>
				{references.map((reference, index) => {
					return <ReferenceSummaryBox key={index} item={reference} />;
				})}

				<button
					type="button"
					className="mt-3 flex cursor-pointer items-center gap-3 font-semibold text-primary-700"
					onClick={addAnotherEduHandler}
				>
					<Plus className="size-4" />
					Add another reference
				</button>

				<div className="mt-4 flex w-full items-center justify-end gap-3">
					<Button type="button" onClick={() => navigate("/resume/extras")} variant="ghost">
						Skip for now
					</Button>
					<Button
						type="button"
						onClick={() => {
							navigate("/resume/extras");
						}}
					>
						Continue
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReferenceSummary;
